import React, { useState } from 'react'
import { IJsonRPCError } from '../../types/IJsonRPCError';
import { catchErrorMessage } from '../../helpers/sharedFunctions';
import { RingLoader } from 'react-spinners';

import Menu from '../../components/Menu/Menu'
import iconEtherium from '../../assets/images/ethereum-icon-light.svg'
import openseaIcon from '../../assets/images/open-sea-icon.svg'
import NFTShowcase from '../../components/NFTShowcase/NFTShowcase'
import useIsPaused from '../../hooks/useIsPaused'
import useMintPrice from '../../hooks/useMintPrice'
import useTotalSupply from '../../hooks/useTotalSupply';
import BigNumber from 'bignumber.js';
import Button from '../../components/Button/Button';
import useGalleryTokens from '../../hooks/useGalleryTokens';
import useFetchTokens from '../../hooks/useFetchTokens';
import ConnectButton from '../../components/ConnectButton/ConnectButton';
import useMaxMint from '../../hooks/useMaxMint';
import { universeContract } from '../../utils/contracts';
import { useWeb3Context } from '../../hooks/useWeb3Context';
import { onChainProvider } from '../../types/OnChainProvider';

interface Props { }

const Home: React.FC<Props> = (props) => {
    const quantityButtons = [5, 10, 15, 20, 25];
    const [isViewingCollection, setIsViewingCollection] = useState(false)
    const [refreshGallery, setRefreshGallery] = useState(false)
    const [refreshMaxMint, setRefreshMaxMint] = useState(false)
    const [refreshSupply, setRefreshSupply] = useState(false)
    const [quantity, setQuantity] = useState(1);
    const [transactionLog, setTransactionLog] = useState([]);
    const [mintLoadingState, setMintLoadingState] = useState({
        isLoading: false,
        title: '',
        description: ''
    });

    // Hooks
    const { provider, address, hasToSwitchNetwork }: onChainProvider = useWeb3Context();
    const { isPaused, isPausedLoading } = useIsPaused();
    const { priceInEther, priceInWei, priceIsInvalid } = useMintPrice(quantity);
    const { totalSupply, mintedCount, isSoldOut, isSupplyLoading } = useTotalSupply(refreshSupply);
    const { galleryNFTs, galleryNFTsIsLoading } = useGalleryTokens(refreshGallery);
    const { mintedNFTs, mintedNFTsIsLoading, setMintedNFTs, setMintedNFTsIsLoading } = useFetchTokens(transactionLog, quantity, setIsViewingCollection, setMintLoadingState);
    const { mintAmountLeft } = useMaxMint(refreshMaxMint);

    /* 
    * @notice: This will disable the mint button if one of these variables are true
    */
    const disableMint = () => {
        if (new BigNumber(quantity).gt(mintAmountLeft)) return true;
        if (hasToSwitchNetwork) return true;
        if (isPaused) return true;
        if (isSoldOut) return true;
        if (!address) return true;
        if (priceIsInvalid) return true;
        if (new BigNumber(mintedCount.toString()).minus(totalSupply.toString()).lt(quantity)) return true;
        return false;
    }

    /* 
 * @notice: After the mint has been succeeded
 */
    const onMintSuccess = (txLog: any) => {
        setTransactionLog(txLog);
        setMintLoadingState({
            isLoading: true,
            title: 'Retrieving metadata',
            description: 'We are almost there. Any moment now...'
        });

        setTransactionLog([])
        setRefreshSupply(true);
        setRefreshMaxMint(true);
        setRefreshGallery(true);

        setTimeout(() => {
            setRefreshSupply(false);
            setRefreshMaxMint(false);
            setRefreshGallery(true);
        }, 2000)
    }

    /* 
    * @notice: Triggers the public mint
    */
    const publicSaleMint = async () => {
        try {
            setMintLoadingState({
                isLoading: true,
                title: 'Waiting for confirmation',
                description: 'Confirm the transaction for further progression'
            });
            const signer = provider.getSigner();
            const options = { value: priceInWei }
            const publicSaleMint = await universeContract(signer).publicSaleMint(quantity, options);
            if (publicSaleMint.hash) {
                setMintLoadingState({
                    isLoading: true,
                    title: 'Minting',
                    description: 'Track your wallet for further details and progression.'
                });
            }
            const txLog = await publicSaleMint.wait();
            if (txLog) {
                onMintSuccess(txLog);
            }
        } catch (err: unknown) {
            const rpcError = err as IJsonRPCError
            const message = catchErrorMessage(rpcError);
            setMintLoadingState({ isLoading: true, title: 'Transaction Failed', description: message });
            setTimeout(() => {
                setMintLoadingState({ isLoading: false, title: 'Transaction Failed', description: message });
            }, 1500)
        }
    }

    const renderMainTitle = () => {
        if (isPausedLoading || isSupplyLoading) return 'LOADING...';
        if (isSoldOut) return 'AI UNIVERSE';
        if (isPaused) return 'PAUSED';
        return 'ENTER YOUR AI UNIVERSE';
    }

    const resetMintedTokens = () => {
        if (mintedNFTs.length > 0) {
            setMintedNFTs([]);
            setMintedNFTsIsLoading(true);
        }
        setRefreshGallery(true);
        setIsViewingCollection(!isViewingCollection);
    }

    const renderMintButton = () => {
        const isLoading = isSupplyLoading || !address || isPausedLoading
        if (!address) return (<ConnectButton className="button btn-main mint-btn" />)
        if (isLoading) return (
            <Button
                disabled
                id="mint-btn"
                style={{ minWidth: 100 }}
                className='button btn-main mint-btn'>
                <span>LOADING...</span>
            </Button>
        )

        let btnText: string = '';

        if (isLoading)
            btnText = 'LOADING...';
        else if (hasToSwitchNetwork)
            btnText = 'WRONG NETWORK';
        else if (!address)
            btnText = 'MINT';
        else if (isPaused)
            btnText = 'PAUSED';
        else if (isSoldOut)
            btnText = 'SOLD OUT'
        else if (new BigNumber(mintedCount.toString()).minus(totalSupply.toString()).lt(quantity))
            btnText = `${new BigNumber(mintedCount.toString()).minus(totalSupply.toString())} ${new BigNumber(mintedCount.toString()).minus(totalSupply.toString()).gt(1) ? "AI UNIVERSE's" : "AI UNIVERSE"} LEFT`
        else
            btnText = 'MINT';

        return (
            <Button
                id="mint-btn"
                style={{ minWidth: 100 }}
                disabled={disableMint()}
                className='button btn-main mint-btn'
                onClick={publicSaleMint}>
                <span>{btnText}</span>
            </Button>
        )
    }

    const renderMint = () => {
        return (
            <div className={`block-main ${(!isViewingCollection && !mintLoadingState.isLoading) ? 'active' : ''}`}>
                <div className="block-content">
                    <div className="block-content-left">
                        <div className="block-content-bg"></div>
                    </div>
                    <div className="block-content-right">

                        <div className="banner-main-top">
                            <h1>{renderMainTitle()}</h1>
                            <p>The Infinite Universe Alliance is a collection of 10,000 unique Captains(and Gods!) that inhabit the Infinite Universe. The collection was created using a structured prompt with distinct elements that determine the NFT's attributes.</p>
                        </div>
                        <div className="banner-main-price-block">
                            <div className="banner-main-price-icon">
                                <img src={iconEtherium} alt="Etherium" />
                            </div>
                            <div className="banner-main-price">
                                <div>{priceInEther}</div>
                                <span>ETH</span>
                            </div>
                        </div>
                        <div className="banner-main-input">
                            <div className="banner-main-input-buttons" id="banner-main-input-btm">
                                {quantityButtons.map((quantity: number) => {
                                    return <Button
                                        disabled={isSoldOut || !address}
                                        key={quantity}
                                        style={{ width: 100 / quantityButtons.length + '%' }}
                                        className="button chip-btn"
                                        onClick={() => setQuantity(quantity)}>
                                        <span>{quantity}</span>
                                    </Button>
                                })}
                            </div>
                            <div
                                className="banner-main-input-top"
                                id="banner-main-input-top">
                                <input disabled={isSoldOut || !address} value={quantity} onChange={(e: any) => setQuantity(e.target.value)} />
                                {renderMintButton()}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="block-socials">
                    <p>© Copyright. {new Date().getFullYear()}. Ai Universe</p>
                    <div className="block-social-wrapper">
                        <a className="block-social" target="_blank" href="https://opensea.io/collection/a-i-universe" rel="noreferrer">
                            <img src={openseaIcon} alt="Opensea" />
                        </a>
                        <a className="block-social" target="_blank" href="https://twitter.com/AI_UniverseNFT" rel="noreferrer">
                            <i className="fa-brands fa-twitter"></i>
                        </a>
                        <a className="block-social" target="_blank" href="https://t.me/+UBxEce4OUVAeoTOn" rel="noreferrer">
                            <i className="fa-brands fa-telegram"></i>
                        </a>
                        <a className="block-social" target="_blank" href="https://discord.com/invite/gMvN2S5Rpm" rel="noreferrer">
                            <i className="fa-brands fa-discord"></i>
                        </a>
                    </div>
                </div>
            </div>
        )
    }

    const renderLoader = () => {
        return (
            <div className={`block-main block-loader ${(!isViewingCollection && mintLoadingState.isLoading) ? 'active' : ''}`}>
                <div className="block-content">
                    <div className="block-content-right">
                        <RingLoader />
                        <div className="banner-main-top">
                            <h1>{mintLoadingState.title}</h1>
                            <p>{mintLoadingState.description}</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderShowcase = () => {
        return (
            <div className={`block-main ${(isViewingCollection && !mintLoadingState.isLoading) ? 'active' : ''}`}>
                <div className="block-content">
                    <div className="block-content-left">
                        <NFTShowcase
                            isLoading={!address ? false : mintedNFTs.length > 0 ? mintedNFTsIsLoading : galleryNFTsIsLoading}
                            nfts={mintedNFTs.length > 0 ? mintedNFTs : galleryNFTs}
                        />
                    </div>
                    <div className="block-content-right">
                        <div className="banner-main-top">
                            <h1>
                                {
                                    mintedNFTs.length > 0
                                        ? `CONGRATS, YOU BOUGHT ${mintedNFTs.length} ${mintedNFTs.length === 1 ? "AI UNIVERSE" : "AI UNIVERSE's"}`
                                        : `Gallery`
                                }
                            </h1>
                            <p>
                                {
                                    galleryNFTsIsLoading
                                        ? "The Infinite Universe Alliance is a collection of 10,000 unique Captains(and Gods!) that inhabit the Infinite Universe. The collection was created using a structured prompt with distinct elements that determine the NFT's attributes."
                                        : mintedNFTs.length > 0
                                            ? "Thank you so much for your support and becoming a member of the AI Universe family."
                                            : galleryNFTs.length <= 0
                                                ? "You haven't added any `AI Universe` to your collection yet, click the button down below to start minting!"
                                                : "The Infinite Universe Alliance is a collection of 10,000 unique Captains(and Gods!) that inhabit the Infinite Universe. The collection was created using a structured prompt with distinct elements that determine the NFT's attributes."
                                }
                            </p>
                        </div>
                        <div className="block-content-buttons">
                            <Button className="button" onClick={() => resetMintedTokens()}>
                                {galleryNFTs <= 0 ? 'START MINTING' : 'MINT MORE'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id="main-wrapper">
            <div id="bg-wrapper"></div>
            <Menu
                disableGalleryClick={mintLoadingState.isLoading}
                disableConnectClick={mintLoadingState.isLoading}
                onGalleryClick={() => resetMintedTokens()}
            />
            <div className="block-wrapper">
                {renderMint()}
                {renderLoader()}
                {renderShowcase()}
            </div>
        </div>
    )
}

export default Home
